import gql from 'graphql-tag';
import React, { FC, useCallback } from 'react';
import { components } from 'react-select';
import { Client, useClient } from 'urql';

import { makeBrandThumbnail } from '@/utils/makeBrandThumbnail';
import { makeFluidThumbnail } from '@/utils/makeFluidThumbnail';
import { SearchAndSelectField } from '../form';
import {
  PlayersSearchQuery,
  PlayersSearchQueryVariables,
} from './__generated__/PlayersSearch';
import { usePlayersByIds } from './usePlayersByIds';

const PLAYERS_SEARCH_QUERY = gql`
  query PlayersSearch($query: String!) {
    viewer {
      id
      playersAsYouTypeV2(
        firstName: $query
        lastName: $query
        playerId: $query
      ) {
        edges {
          node {
            uuid
            playerId
            firstName
            lastName
            brand {
              id
              code
            }
          }
        }
      }
    }
  }
`;

type Props = {
  id: string;
  name: string;
  title: string;
  required?: boolean;
};

type PlayerProps = {
  uuid: string;
  firstName: string;
  lastName: string;
};

//TODO: This should be typed as OptionProps<OptionType> once the components are refactored
// Ticket: DEV-261
const OptionComponent: FC<any> = (props) => {
  const { children, data } = props;

  const fluid = data.value.brand
    ? makeBrandThumbnail(data.value.brand)
    : data.value.thumbnail
      ? makeFluidThumbnail(data.value.thumbnail, data.value.thumbnailUpdatedAt)
      : null;

  return (
    <components.Option {...props}>
      <div className="flex flex-col flex-o w-full pl-2 p-1 cursor-pointer hover:bg-gray-200">
        <div className="flex flex-grow overflow-hidden overflow-ellipsis whitespace-nowrap">
          {fluid ? (
            <picture className="flex-none">
              <source sizes={fluid.sizes} srcSet={fluid.srcSet} />
              <img
                src={fluid.src}
                alt=""
                loading="lazy"
                className="w-6 h-6 rounded-sm mr-2"
              />
            </picture>
          ) : (
            <div className="w-6 h-6 rounded-sm mr-2 bg-gray-300" />
          )}
          {children}
        </div>
        <div className="text-xs text-gray-500">{data.value.uuid}</div>
      </div>
    </components.Option>
  );
};

const optionObject = ({ value }: any) => {
  return {
    itemId: value.uuid,
    name: `${value.firstName} ${value.lastName}`,
  };
};

const loadOptions = (client: Client) => (query: string, callback: Function) => {
  if (query.length > 0) {
    const players = client
      .query<PlayersSearchQuery, PlayersSearchQueryVariables>(
        PLAYERS_SEARCH_QUERY,
        {
          query,
        },
      )
      .toPromise();

    Promise.all([players]).then(([playerRes]) => {
      const p =
        playerRes?.data?.viewer.playersAsYouTypeV2?.edges?.map((edge) => ({
          value: edge?.node,
          label: `${edge?.node.firstName} ${edge?.node.lastName}`,
        })) || [];

      callback([...p]);
    });
  } else {
    callback([]);
  }
};

const listItem = (item: PlayerProps) => ({
  itemId: item.uuid,
  name: `${item.firstName} ${item.lastName}`,
});

export const PlayersSearch = ({ id, name, title, ...props }: Props) => {
  const { players, fetching, setPlayerIds, batchProcessing } = usePlayersByIds({
    enableBatchProcessing: true, // Enable batch processing for bulk imports
  });

  const client = useClient();

  const onImportBulk = useCallback(
    (playerIds: string[] | undefined) => {
      if (!playerIds || playerIds.length === 0) {
        return { results: [], loading: false };
      }

      setPlayerIds(playerIds);

      return {
        results: players || [],
        loading: fetching,
      };
    },
    [players, setPlayerIds, fetching],
  );

  return (
    <SearchAndSelectField
      required={props.required}
      id={id}
      name={name}
      title={title}
      loadOptions={loadOptions(client)}
      component={<OptionComponent />}
      isAsync
      isMultiple
      config={{
        enableBulk: true,
        option: optionObject,
        defaultAttrName: 'uuid',
        onImportBulk,
        listItem,
        batchProcessing,
      }}
    />
  );
};
